import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface PurchaseOrder {
  id?: number;
  orderNumber: string;
  supplierName: string;
  supplierId?: number;
  orderDate: string;
  expectedDeliveryDate: string;
  actualDeliveryDate?: string;
  status: 'Pending' | 'Processing' | 'Shipped' | 'Delivered' | 'Cancelled';
  totalAmount: number;
  itemCount: number;
  createdBy: string;
  approvedBy?: string;
  approvedAt?: string;
  notes?: string;
  items?: PurchaseOrderItem[];
  createdAt?: string;
  updatedAt?: string;
}

export interface PurchaseOrderItem {
  id?: number;
  purchaseOrderId?: number;
  productCode: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  specifications?: string;
  notes?: string;
}

export interface PurchaseOrderStats {
  totalOrders: number;
  pendingOrders: number;
  processingOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  totalValue: number;
  averageOrderValue: number;
  topSuppliers: {
    supplierName: string;
    orderCount: number;
    totalValue: number;
  }[];
  monthlyTrends: {
    month: string;
    orderCount: number;
    totalValue: number;
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PurchaseOrdersService {
  private apiUrl = `${environment.apiServiceBaseUrl}/hawdco/purchase-orders`;

  constructor(private http: HttpClient) {}

  // Get all purchase orders with optional pagination and filtering
  getPurchaseOrders(
    page: number = 1, 
    limit: number = 10, 
    search?: string, 
    status?: string,
    supplier?: string,
    dateFrom?: string,
    dateTo?: string
  ): Observable<ApiResponse<PurchaseOrder[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    if (supplier) params = params.set('supplier', supplier);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get<ApiResponse<PurchaseOrder[]>>(this.apiUrl, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get purchase order by ID
  getPurchaseOrderById(id: number): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.get<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Create new purchase order
  createPurchaseOrder(order: PurchaseOrder): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.post<ApiResponse<PurchaseOrder>>(this.apiUrl, order)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update purchase order
  updatePurchaseOrder(id: number, order: PurchaseOrder): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.put<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${id}`, order)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Delete purchase order
  deletePurchaseOrder(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Approve purchase order
  approvePurchaseOrder(id: number, approvedBy: string): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.post<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${id}/approve`, { approvedBy })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Cancel purchase order
  cancelPurchaseOrder(id: number, reason: string): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.post<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${id}/cancel`, { reason })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update purchase order status
  updateOrderStatus(id: number, status: string, notes?: string): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.put<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${id}/status`, { status, notes })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Mark order as delivered
  markAsDelivered(id: number, deliveryDate: string, receivedBy: string): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.post<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${id}/deliver`, {
      deliveryDate,
      receivedBy
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Get purchase order statistics
  getPurchaseOrdersStats(dateFrom?: string, dateTo?: string): Observable<ApiResponse<PurchaseOrderStats>> {
    let params = new HttpParams();
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get<ApiResponse<PurchaseOrderStats>>(`${this.apiUrl}/stats`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get order statuses
  getOrderStatuses(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/statuses`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get suppliers for dropdown
  getSuppliers(): Observable<ApiResponse<{ id: number; name: string }[]>> {
    return this.http.get<ApiResponse<{ id: number; name: string }[]>>(`${this.apiUrl}/suppliers`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Add items to purchase order
  addOrderItems(orderId: number, items: PurchaseOrderItem[]): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.post<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${orderId}/items`, { items })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update order item
  updateOrderItem(orderId: number, itemId: number, item: PurchaseOrderItem): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.put<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${orderId}/items/${itemId}`, item)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Remove order item
  removeOrderItem(orderId: number, itemId: number): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.delete<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/${orderId}/items/${itemId}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Generate purchase order number
  generateOrderNumber(): Observable<ApiResponse<string>> {
    return this.http.get<ApiResponse<string>>(`${this.apiUrl}/generate-number`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Validate purchase order before creating
  validatePurchaseOrder(order: PurchaseOrder): Observable<ApiResponse<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
    estimatedTotal: number;
  }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/validate`, order)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Export purchase orders to Excel
  exportToExcel(search?: string, status?: string, supplier?: string, dateFrom?: string, dateTo?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    if (supplier) params = params.set('supplier', supplier);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get(`${this.apiUrl}/export/excel`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Export purchase orders to PDF
  exportToPDF(search?: string, status?: string, supplier?: string, dateFrom?: string, dateTo?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    if (supplier) params = params.set('supplier', supplier);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get(`${this.apiUrl}/export/pdf`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Get purchase order by order number
  getPurchaseOrderByNumber(orderNumber: string): Observable<ApiResponse<PurchaseOrder>> {
    return this.http.get<ApiResponse<PurchaseOrder>>(`${this.apiUrl}/number/${orderNumber}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Error handling
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error('Purchase Orders API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
